import type { QueryClient } from 'react-query';
import axios, { AxiosError, AxiosInstance, AxiosPromise, AxiosResponse } from 'axios';
import { QueryKeys } from 'constants/queryKeys';
import type {
  PaginationParams,
  ConfirmPayload,
  ConfirmResponse,
  LoginPayload,
  LoginResponse,
  SuccessResponse,
  EmployerResponse,
  EmployerPayload,
  EmployersResponse,
  TokenData,
  IraUsersResponse,
  IraUserResponse,
  IraUserRequest,
  LockUserRequest,
  ProfileResponse,
  ForgotPasswordConfirmationPayload,
  ForgotPasswordConfirmationResponce,
  ChangePasswordPayload,
  ConfirmChangingPasswordPayload,
  SendEmailResponce,
  ResetPasswordPayload,
  SendEmailRequest,
  ForgotPasswordConfirmationRequest,
  ResetPasswordRequest,
  ID,
  DocumentsResponce,
  SendCodeResponse,
  IndependentIraUser,
  IndependentIraUsersResponse,
  IndependentIraUserRequest,
  ConfirmChangingPasswordResponse,
  ProfileListResponse,
  ContributionsResponse,
  ImportContributionsResponse,
  ContributionDetailsResponse,
  PortfolioAssetsResponse,
  AllAssetsResponse,
  PortfolioBalanceResponse,
  PortfolioTransactionsResponse,
  ExportUsersPayload,
  IndependentIraUsersMetricsPayload,
  IndependentIraUsersMetricsResponse,
  DefaultUserManagersResponse,
  ContributeWalletTransferPayload,
  UsersToAssignWalletTransferResponse,
  RevenueByMonthResponse,
  GenerateAccountStatementPayload,
  InflowOutflowByMonthResponse,
} from './requests';

import handleRefreshToken from './api.interceptors';
import { ApiError } from './api.errors';

const API_URL = process.env.REACT_APP_API_URL;
const ACCESS_TOKEN = 'accessToken';
const REFRESH_TOKEN = 'refreshToken';
const RESET_PASSWORD_TOKEN = 'resetPasswordToken';

export class Api {
  static _retry = false;
  static readonly axios: AxiosInstance = axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Security-Policy': "frame-ancestors 'none'",
      'X-Frame-Options': 'DENY',
    },
  });
  static QueryClient: QueryClient;

  constructor() {
    Api.axios.interceptors.response.use(
      (response) => response,
      handleRefreshToken.bind(this),
    );

    this.setTokens(this.accessToken ?? '', null);
  }

  // Sign In
  public login = (payload: LoginPayload) =>
    Api.axios.post<LoginResponse>('/account/login', payload);

  public getProfile = () => Api.axios.get<ProfileResponse>('/profile');

  public static clearAccessToken = () => {
    delete Api.axios.defaults.headers.common.Authorization;
  };

  public refreshAccessToken = (refreshToken: string) =>
    Api.axios.post<TokenData>('/account/refresh', {
      token: refreshToken,
    });

  public sendCodeTo = () => Api.axios.post<SendCodeResponse>('/account/sign-in/send');

  public confirm = (payload: ConfirmPayload) =>
    Api.axios.post<ConfirmResponse>('/account/sign-in/confirmation', payload);

  public logout = () => Api.axios.post<SuccessResponse>('/account/logout');

  // Employers
  public getEmployers = (params: PaginationParams) =>
    Api.axios.get<EmployersResponse>('/employers', { params });

  public getEmployer = (id: ID) => Api.axios.get<EmployerResponse>(`/employer/${id}`);

  public createEmployer = (payload: EmployerPayload) =>
    Api.axios.post<EmployerResponse>('/employer', payload);

  public editEmployer = (
    id: ID | undefined,
    payload: EmployerPayload,
  ): AxiosPromise<EmployerResponse> =>
    Api.axios.put<EmployerPayload, AxiosResponse<EmployerResponse>>(
      `/employer/${id}`,
      payload,
    );

  // IRA users
  public getIraUsers = (
    id: ID | null | undefined,
    payload: PaginationParams,
  ): AxiosPromise<IraUsersResponse> =>
    Api.axios.post<IraUsersResponse>(
      id ? `/employer/${id}/users/ira/list` : `/employer/users/ira/list`,
      payload,
    );

  public getIraUser = (id: ID): AxiosPromise<IraUserResponse> =>
    Api.axios.get<IraUserResponse>(`/user/ira/${id}`);

  public createIraUser = (
    id: ID | undefined,
    payload: IraUserRequest,
  ): AxiosPromise<IraUserResponse> =>
    Api.axios.post<IraUserResponse, AxiosResponse<IraUserResponse>, IraUserRequest>(
      id ? `/employer/${id}/user/ira` : `/employer/user/ira`,
      payload,
    );

  public editIraUser = (
    id: ID | undefined,
    payload: Partial<IraUserRequest>,
  ): AxiosPromise<IraUserResponse> =>
    Api.axios.patch<Partial<IraUserRequest>, AxiosResponse<IraUserResponse>>(
      `/user/ira/${id}`,
      payload,
    );

  public inviteIraUser = (id: ID): AxiosPromise<SuccessResponse> =>
    Api.axios.post<SuccessResponse>(`/user/ira/${id}/invitation/send`);

  public resendInviteIraUser = (id: ID) =>
    Api.axios.post<SuccessResponse>(`/user/ira/${id}/invitation/resend`);

  public lockIraUser = (
    id: ID | undefined,
    payload: LockUserRequest,
  ): AxiosPromise<SuccessResponse> =>
    Api.axios.post<SuccessResponse, AxiosResponse<SuccessResponse>, LockUserRequest>(
      `/user/ira/${id}/lock`,
      payload,
    );

  public unlockIraUser = (
    id: ID | undefined,
    isAutoLocked: boolean,
  ): AxiosPromise<SuccessResponse> =>
    Api.axios.post<SuccessResponse>(
      isAutoLocked ? `/user/ira/${id}/autolock/cancel` : `/user/ira/${id}/unlock`,
    );

  public checkIraUserEmail = (
    id: ID | undefined,
    email: SendEmailRequest['email'],
  ): AxiosPromise<IndependentIraUser> =>
    Api.axios.post<
      IndependentIraUser,
      AxiosResponse<IndependentIraUser>,
      SendEmailRequest
    >(id ? `/employer/${id}/user/ira/email/check` : `employer/user/ira/email/check`, {
      email,
    });

  //CHANGE PASSWORD
  public changePassword = (
    payload: ChangePasswordPayload,
  ): AxiosPromise<SendEmailResponce> =>
    Api.axios.post<SendEmailResponce>(`/account/change-password/send`, {
      ...payload,
    });

  public confirmChangingPassword = (
    payload: ConfirmChangingPasswordPayload,
  ): AxiosPromise<ConfirmChangingPasswordResponse> =>
    Api.axios.post<ConfirmChangingPasswordResponse>(
      `/account/change-password/confirmation`,
      {
        ...payload,
      },
    );

  //FORGOT PASSWORD
  public sendForgotPassword = (
    email: SendEmailRequest['email'],
  ): AxiosPromise<SendEmailResponce> =>
    Api.axios.post<SendEmailResponce, AxiosResponse<SendEmailResponce>, SendEmailRequest>(
      `/account/forgot-password/email/send`,
      {
        email,
      },
    );

  public confirmForgotPassword = (
    payload: ForgotPasswordConfirmationPayload,
  ): AxiosPromise<ForgotPasswordConfirmationResponce> =>
    Api.axios.post<
      ForgotPasswordConfirmationResponce,
      AxiosResponse<ForgotPasswordConfirmationResponce>,
      ForgotPasswordConfirmationRequest
    >(`/account/forgot-password/email/confirmation`, {
      ...payload,
    });

  public resetPassword = (payload: ResetPasswordPayload): AxiosPromise<SuccessResponse> =>
    Api.axios.post<SuccessResponse, AxiosResponse<SuccessResponse>, ResetPasswordRequest>(
      `/account/password/reset`,
      {
        ...payload,
        token: this.resetPasswordToken,
      },
    );

  //DOCUMENTS
  public getDocuments = (): AxiosPromise<DocumentsResponce> =>
    Api.axios.get<DocumentsResponce>('/documents');

  //IRA-ACCOUNTS
  public getIndependentIraUsers = (
    payload: PaginationParams,
  ): AxiosPromise<IndependentIraUsersResponse> =>
    Api.axios.post<IndependentIraUsersResponse>('/profile/list', payload);

  public getIndependentIraUsersMetrics = (
    payload: IndependentIraUsersMetricsPayload,
  ): AxiosPromise<IndependentIraUsersMetricsResponse> =>
    Api.axios.post<IndependentIraUsersMetricsResponse>('/profile/list/totals', payload);

  public exportIndependentIraUsers = (payload: ExportUsersPayload): AxiosPromise<Blob> =>
    Api.axios.post<Blob>('/profile/list/export', payload, {
      responseType: 'blob',
    });

  public getIndependentIraUser = (id: ID): AxiosPromise<IndependentIraUser> =>
    Api.axios.get<IndependentIraUser>(`/user/profile/${id}`);

  public checkIndependentIraEmail = (
    email: SendEmailRequest['email'],
  ): AxiosPromise<IndependentIraUser> =>
    Api.axios.post<
      IndependentIraUser,
      AxiosResponse<IndependentIraUser>,
      SendEmailRequest
    >(`/user/profile/email/check`, { email });

  public addIndependentIraUser = (
    payload: IndependentIraUserRequest,
  ): AxiosPromise<IndependentIraUser> =>
    Api.axios.post<
      IndependentIraUser,
      AxiosResponse<IndependentIraUser>,
      IndependentIraUserRequest
    >(`/user/profile`, { ...payload });

  public editIndependentIraUser = (
    id: ID | undefined,
    payload: Partial<IndependentIraUserRequest>,
  ): AxiosPromise<IndependentIraUser> =>
    Api.axios.patch<Partial<IndependentIraUser>, AxiosResponse<IndependentIraUser>>(
      `/user/profile/${id}`,
      payload,
    );

  public inviteIndependentIraUser = (id: ID): AxiosPromise<SuccessResponse> =>
    Api.axios.post<SuccessResponse>(`/user/profile/${id}/invitation/send`);

  public resendInviteIndependentIraUser = (id: ID) =>
    Api.axios.post<SuccessResponse>(`/user/profile/${id}/invitation/resend`);

  public lockIndependentIraUser = (
    id: ID | undefined,
    payload: LockUserRequest,
  ): AxiosPromise<SuccessResponse> =>
    Api.axios.post<SuccessResponse, AxiosResponse<SuccessResponse>, LockUserRequest>(
      `/user/profile/${id}/lock`,
      payload,
    );

  public unlockIndependentIraUser = (
    id: ID | undefined,
    isAutoLocked: boolean,
  ): AxiosPromise<SuccessResponse> =>
    Api.axios.post<SuccessResponse>(
      isAutoLocked ? `/user/profile/${id}/autolock/cancel` : `/user/profile/${id}/unlock`,
    );

  public retryAccountCreation = (
    id: ID,
    userType: string,
  ): AxiosPromise<SuccessResponse> =>
    Api.axios.post<SuccessResponse>(`/${userType}/${id}/activate`);

  //PROFILE
  public getProfileList = (email?: string): AxiosPromise<ProfileListResponse> =>
    Api.axios.post<ProfileListResponse>(`/user/profile/get-list`, { email });

  //USER MANAGER
  public getDefaultUserManagers = (): AxiosPromise<DefaultUserManagersResponse> =>
    Api.axios.get<DefaultUserManagersResponse>('users/user-manager/defaults');

  // CONTRIBUTIONS
  public getContributions = (): AxiosPromise<ContributionsResponse> =>
    Api.axios.get<ContributionsResponse>('/users/ira/wallet-transfers');

  public importContributions = (payload: FormData) =>
    Api.axios.post<
      ImportContributionsResponse,
      AxiosResponse<ImportContributionsResponse>,
      FormData
    >('/users/ira/import/funding-transfers/upload', payload, {
      headers: { 'Content-Type': 'multipart/form-data;boundary="boundary"' },
    });

  public getContributionDetails = (id: ID | undefined) =>
    Api.axios.get<ContributionDetailsResponse>(
      `/users/ira/wallet-transfers/${id}/details`,
    );

  public refreshContributions = (): AxiosPromise<SuccessResponse> =>
    Api.axios.post<SuccessResponse>(`/ledger/wallet-transfers/sync`);

  public unsubscribeInvitation = (accessKey: string): AxiosPromise<SuccessResponse> =>
    Api.axios.post<SuccessResponse>(
      `/user/profile/email/invitation/unsubscribe?accessKey=${accessKey}`,
    );

  public getUsersToAssignWalletTransfer = (
    walletTransferExternalId?: ID,
  ): AxiosPromise<UsersToAssignWalletTransferResponse> =>
    Api.axios.get<UsersToAssignWalletTransferResponse>(
      `/users/ira/wallet-transfers/users-to-assign/?walletTransferExternalId=${walletTransferExternalId}`,
    );

  public assignWalletTransfer = (
    payload: ContributeWalletTransferPayload,
  ): AxiosPromise<SuccessResponse> =>
    Api.axios.post<SuccessResponse>('/users/ira/wallet-transfers/assign', payload);

  // PORTFOLIO
  public getAllAssets = () => Api.axios.get<AllAssetsResponse>(`/assets`);

  public getProfileAssets = (profileId: number | undefined) =>
    Api.axios.get<PortfolioAssetsResponse>(
      `/profile/${profileId}/assets?showZeroAssets=true`,
    );

  public getProfileTransactions = (
    profileId: number | undefined,
    startDate: string,
    endDate: string,
    timeZoneId: string,
  ) => {
    const rangeParams = new URLSearchParams();
    rangeParams.append('startDate', startDate);
    rangeParams.append('endDate', endDate);
    rangeParams.append('timeZoneId', timeZoneId);

    return Api.axios.get<PortfolioTransactionsResponse>(
      `/profile/${profileId}/transactions?${rangeParams.toString()}`,
    );
  };

  public getProfileBalance = (profileId: number | undefined) =>
    Api.axios.get<PortfolioBalanceResponse>(`/portfolio/${profileId}/balance`);

  public generateAccountStatement = (
    payload: GenerateAccountStatementPayload,
  ): AxiosPromise<Blob> =>
    Api.axios.post<Blob>('/profile-balances/report/pdf', payload, {
      responseType: 'blob',
    });

  //ETZ PROFILE
  public getEtzProfileBalance = () =>
    Api.axios.get<PortfolioBalanceResponse>(`/portfolio/etz/balance`);

  public getEtzProfileAssets = () =>
    Api.axios.get<PortfolioAssetsResponse>(`/profile/etz/assets?showZeroAssets=true`);

  public getEtzProfileTransactions = (
    startDate: string,
    endDate: string,
    timeZoneId: string,
  ) => {
    const rangeParams = new URLSearchParams();
    rangeParams.append('startDate', startDate);
    rangeParams.append('endDate', endDate);
    rangeParams.append('timeZoneId', timeZoneId);

    return Api.axios.get<PortfolioTransactionsResponse>(
      `/profile/etz/transactions?${rangeParams.toString()}`,
    );
  };

  public getEtzProfileRevenueByMonth = (params: PaginationParams) =>
    Api.axios.get<RevenueByMonthResponse>('/profile/etz/revenue', { params });

  public exportEtzProfileRevenueByMonth = (): AxiosPromise<Blob> =>
    Api.axios.post<Blob>(
      '/profile/etz/revenue/export',
      {},
      {
        responseType: 'blob',
      },
    );

  public getEtzProfileInflowOutflowByMonth = (params: PaginationParams) =>
    Api.axios.get<InflowOutflowByMonthResponse>('/profile/etz/outflow-inflow', {
      params,
    });

  //TOKENS
  public setTokens = (
    accessToken: string,
    refreshToken: string | null,
    shouldSaveTokens: boolean = true,
  ) => {
    if (accessToken) {
      Api.axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    }

    if (shouldSaveTokens && refreshToken) {
      sessionStorage.setItem(ACCESS_TOKEN, accessToken);
      sessionStorage.setItem(REFRESH_TOKEN, refreshToken);
      window.dispatchEvent(new Event('storageAdd'));
    }
  };

  public setResetPasswordToken = (resetPasswordToken: string) => {
    sessionStorage.setItem(RESET_PASSWORD_TOKEN, resetPasswordToken);
  };

  public static removeTokens(): void {
    sessionStorage.removeItem(ACCESS_TOKEN);
    sessionStorage.removeItem(REFRESH_TOKEN);
    sessionStorage.removeItem(RESET_PASSWORD_TOKEN);
    window.dispatchEvent(new Event('storageClear'));
  }

  public logoutApp() {
    Api.QueryClient.clear();
    Api.clearAccessToken();
    Api.removeTokens();
  }

  public logoutBlockedUser(data: AxiosError<ApiError>) {
    this.logout();
    this.logoutApp();
    Api.QueryClient.setQueryData(QueryKeys.userBlocked, data);
  }

  get accessToken(): string | null {
    return sessionStorage.getItem(ACCESS_TOKEN) || null;
  }

  get refreshToken(): string | null {
    return sessionStorage.getItem(REFRESH_TOKEN) || null;
  }

  get resetPasswordToken(): string | null {
    return sessionStorage.getItem(RESET_PASSWORD_TOKEN) || null;
  }

  // React query
  static setQueryClient(QueryClient: QueryClient) {
    Api.QueryClient = QueryClient;
  }
}

const api = new Api();

export default api;
